import React, { createContext, useContext, useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Route definitions — see /src/routes.js. AuthCallback is defined inline below
// (it consumes Auth/Language contexts directly) and is kept here with the other
// provider-aware components.
import { ROUTE_CONFIG } from './routes';
import { hydrateAuthToken, getAuthToken, saveAuthToken, clearAuthToken, getRefreshToken, saveRefreshToken, getCachedUser, saveCachedUser, clearCachedUser } from './lib/authToken';
import { configureRevenueCat, isNativeIAPSupported, logoutRevenueCat } from './lib/revenuecat';
import { PaywallProvider, usePaywall } from './context/PaywallContext';
import BiometricGate from './components/BiometricGate';
import BuildBadge from './components/BuildBadge';
import SystemStatusBanner from './components/SystemStatusBanner';
import { initPostHog, initWebVitals, identifyUser, resetAnalytics, track, EVENTS } from './lib/analytics';
import enTranslations from './i18n/en.json';
import hiTranslations from './i18n/hi.json';
import teTranslations from './i18n/te.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============== CONTEXTS ==============

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

const translations = { en: enTranslations, hi: hiTranslations, te: teTranslations };

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem('jb_language') || 'en');

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations['en'][key] || key;
  }, [language]);

  const changeLanguage = useCallback((lang) => {
    setLanguage(lang);
    localStorage.setItem('jb_language', lang);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage: changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

// Single-flight silent re-login. The refresh token is SINGLE-USE (rotated by
// the server), so concurrent checkAuth invocations (React StrictMode double
// effect, cold-start races) MUST share one in-flight request — a duplicate
// call with the consumed token would 401 and wipe the freshly rotated tokens.
let _refreshInFlight = null;
const silentRefresh = () => {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    const startSession = getAuthToken();
    let rt = null;
    try { rt = await getRefreshToken(); } catch { rt = null; }
    if (!rt) return { status: 'rejected', user: null };
    try {
      const r = await axios.post(`${API}/auth/refresh`, { refresh_token: rt }, { withCredentials: true, timeout: 8000 });
      return { status: 'ok', user: r?.data || null }; // interceptor persists rotated tokens
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 400) {
        // Only wipe if no parallel refresh rotated the tokens meanwhile.
        let nowRt = null;
        try { nowRt = await getRefreshToken(); } catch { nowRt = null; }
        if (nowRt === rt && getAuthToken() === startSession) {
          clearAuthToken();
          clearCachedUser();
        }
        return { status: 'rejected', user: null };
      }
      return { status: 'network', user: null };
    }
  })().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Hard ceiling so the loading screen can never hang forever on native
    // (e.g. if a Capacitor plugin import stalls or the network blocks).
    const timeoutMs = 15000;
    const timer = setTimeout(() => {
      try { setLoading(false); } catch {}
      console.warn('[auth] checkAuth timed out after', timeoutMs, 'ms — proceeding unauthenticated');
    }, timeoutMs);

    try {
      // Hydrate tokens from native Preferences before the first /me call so
      // cold-start logins persist (WKWebView ITP wipes localStorage but
      // Preferences→UserDefaults/SharedPreferences survives).
      await Promise.race([
        hydrateAuthToken(),
        new Promise(r => setTimeout(r, 4000)),
      ]).catch(() => {});

      // /auth/me with ONE retry on network errors. Only a real 401/403 means
      // "logged out" — a timeout on a flaky cold-start network must NOT bounce
      // the user to the login screen (that was the recurring logout bug).
      let me = null;
      let authRejected = false;
      for (let attempt = 0; attempt < 2 && !me && !authRejected; attempt++) {
        try {
          const r = await axios.get(`${API}/auth/me`, { withCredentials: true, timeout: 8000 });
          me = r?.data || null;
        } catch (e) {
          const st = e?.response?.status;
          if (st === 401 || st === 403) authRejected = true;
          else if (attempt === 0) await new Promise(r => setTimeout(r, 1200));
        }
      }

      if (!me && authRejected) {
        // Session invalid — silently re-login with the device refresh token.
        // silentRefresh dedupes concurrent calls (single-use rotated token).
        const res = await silentRefresh();
        if (res.status === 'ok' && res.user) {
          me = res.user;
        } else if (res.status === 'network') {
          // Couldn't reach the server to refresh — keep the user in the app
          // with the cached snapshot; the next cold start retries the refresh.
          const cached = getCachedUser();
          if (cached) me = cached;
        }
        // status 'rejected' → tokens already cleared inside silentRefresh
      } else if (!me && !authRejected) {
        // Pure network failure with a saved token: restore the cached user so
        // the app opens logged-in; the bearer token still rides every request.
        const cached = getCachedUser();
        if (getAuthToken() && cached) me = cached;
      }

      if (me) {
        setUser(me);
        saveCachedUser(me);
        try { identifyUser(me); } catch {}
        try { track(EVENTS.LOGIN_SUCCESS, { auth_provider: me?.auth_provider || 'email' }); } catch {}
        // Configure RevenueCat with this user's id so entitlements follow them
        // across devices. No-op on web (gated by Capacitor.isNativePlatform()).
        if (isNativeIAPSupported() && me?.user_id) {
          try { configureRevenueCat(me.user_id).catch(() => {}); } catch {}
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    // ── STEP 1: clear all client-side state FIRST ────────────────────
    // We do this before the API call so the user is effectively logged
    // out immediately — even if the network hangs, the API returns
    // 403/CSRF, or the browser refuses to send our session cookie
    // (Safari ITP / WKWebView etc.), the UI will still land on the
    // login screen and the auth guard will redirect.
    clearAuthToken();
    clearCachedUser();
    setUser(null);
    resetAnalytics();
    logoutRevenueCat().catch(() => {});

    // ── STEP 2: best-effort server revocation ────────────────────────
    // Fire-and-forget: we don't await on the outer promise chain and
    // failures are swallowed. The backend session is short-lived so
    // even if this fails the token becomes useless on its own.
    let rt = null;
    try { rt = await getRefreshToken(); } catch {}
    axios.post(
      `${API}/auth/logout`,
      rt ? { refresh_token: rt } : {},
      { withCredentials: true }
    ).catch(() => { /* server-side revoke failed — client is already logged out */ });
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== CLINIC BRANDING (white-label) ==============
// When a patient is linked to a doctor whose clinic has custom branding, we
// fetch it once and expose it via useClinicBrand(). The provider also writes
// CSS variables onto the document root so the whole app can switch the accent
// colour with one tap.

const ClinicBrandContext = createContext({ active: false });
export const useClinicBrand = () => useContext(ClinicBrandContext);

const ClinicBrandProvider = ({ children }) => {
  const { user } = useAuth();
  const [brand, setBrand] = useState({ active: false });

  useEffect(() => {
    let cancelled = false;
    const reset = () => {
      document.documentElement.style.removeProperty('--brand-accent');
      document.documentElement.style.removeProperty('--brand-accent-soft');
      setBrand({ active: false });
    };
    if (!user || ['doctor', 'admin'].includes(user?.role)) { reset(); return; }
    axios.get(`${API}/patient/clinic-brand`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        const b = r.data || { active: false };
        setBrand(b);
        if (b.active && b.accent_color) {
          document.documentElement.style.setProperty('--brand-accent', b.accent_color);
          // Cheap soft variant — same hue at low opacity for backgrounds.
          document.documentElement.style.setProperty('--brand-accent-soft', `${b.accent_color}15`);
        } else {
          document.documentElement.style.removeProperty('--brand-accent');
          document.documentElement.style.removeProperty('--brand-accent-soft');
        }
      })
      .catch(reset);
    return () => { cancelled = true; };
  }, [user]);

  return (
    <ClinicBrandContext.Provider value={brand}>{children}</ClinicBrandContext.Provider>
  );
};

// ============== AUTH CALLBACK ==============

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);

    // Always strip the hash so a page reload won't re-trigger a stale session_id
    const stripHash = () => {
      try {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      } catch {}
    };

    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];

      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          stripHash();
          setUser(response.data);
          toast.success('Welcome to Aayi!');

          if (response.data.onboarding_complete) {
            navigate('/dashboard', { replace: true, state: { user: response.data } });
          } else {
            navigate('/onboarding', { replace: true, state: { user: response.data } });
          }
        })
        .catch(() => {
          stripHash();
          // Specific message for expired / already-consumed session_id so users
          // don't get stuck wondering why "Authentication failed" keeps showing.
          toast.error('Your login link expired. Please sign in again.');
          navigate('/', { replace: true });
        });
    } else {
      stripHash();
      navigate('/', { replace: true });
    }
  }, [location, navigate, setUser]);

  return (
    <div className="app-container flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#E05A47] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#57534E]">Signing you in...</p>
      </div>
    </div>
  );
};

// ============== PROTECTED ROUTE ==============

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Iter-150 · Clinic-staff aware routing. Query /clinic/me once per
  // session so nurses / receptionists (role = "patient" in the users
  // table, but linked in clinic_staff) land in the scoped staff portal
  // instead of the mom dashboard.
  const [staffRole, setStaffRole] = useState(undefined);   // undefined = unknown, null = not staff
  useEffect(() => {
    if (!user || user.role === 'doctor' || user.role === 'admin') { setStaffRole(null); return; }
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/clinic/me`, { withCredentials: true });
        if (!alive) return;
        setStaffRole(r.data?.role || null);
      } catch { if (alive) setStaffRole(null); }
    })();
    return () => { alive = false; };
  }, [user?.user_id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Patient-only pages — doctors land on their portal instead. `/profile`,
  // `/trackers`, `/shop` are mother-shaped surfaces (bottom nav + patient
  // API calls). A doctor navigating there via a stale link or bottom nav
  // would fire /doctor-only endpoints on the wrong role or trigger a
  // Bottom-Nav journey into a page that 403s with "Doctor access required".
  // (Iter-155 · reported when an admin-created doctor logged in, hit
  // /profile, tapped a chevron, and got a bare "Doctor access required".)
  const PATIENT_PAGES = [
    '/dashboard', '/advice', '/services', '/vaccinations',
    '/profile', '/trackers', '/shop', '/achievements', '/notification-settings',
  ];
  const STAFF_ONLY_ROLES = ['nurse', 'receptionist'];

  // Redirect decision is centralized here so navigate() is never called during render.
  // Returning a non-null target causes the component to render null until the effect runs.
  const redirectTo = (() => {
    if (loading) return null;
    if (!user) return '/';
    if (staffRole === undefined) return null;   // wait for /clinic/me before deciding
    if (STAFF_ONLY_ROLES.includes(staffRole) && location.pathname !== '/staff-portal') {
      // Nurses & receptionists live on the scoped portal; skip mom onboarding entirely.
      return '/staff-portal';
    }
    // Iter-156 · Doctors get their own 2-step welcome wizard instead of the
    // mother `/onboarding` flow. Also cover doctors whose account was
    // provisioned with a temporary password by an admin — even if their
    // onboarding is already complete they must set a new password first.
    const needsDoctorWelcome =
      user.role === 'doctor'
      && (!user.onboarding_complete || user.must_reset_password);
    if (needsDoctorWelcome && location.pathname !== '/doctor-welcome') {
      return '/doctor-welcome';
    }
    // Iter-156 · reverse guard — a completed doctor manually visiting
    // /doctor-welcome should be bounced to their portal. Without this,
    // the wizard renders again and offers to reset a password that
    // doesn't need resetting.
    if (!needsDoctorWelcome && location.pathname === '/doctor-welcome') {
      return user.role === 'doctor' ? '/doctor-portal' : '/dashboard';
    }
    if (!user.onboarding_complete && location.pathname !== '/onboarding' && location.pathname !== '/doctor-welcome') return '/onboarding';
    if (user.role === 'doctor' && PATIENT_PAGES.includes(location.pathname)) return '/doctor-portal';
    return null;
  })();

  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
    }
  }, [redirectTo, navigate]);

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-12 h-12 border-4 border-[#E05A47] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (redirectTo) return null;

  return children;
};

// ============== APP ROUTER ==============

// Syncs the user's saved language preference (from their profile in DB) into
// the LanguageProvider on login. Also syncs when the user object changes.
const LanguageAuthSync = () => {
  const { user } = useAuth();
  const { language, setLanguage } = useLanguage();
  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.language]);
  return null;
};

// ============== NATIVE (Capacitor) BOOTSTRAP ==============
// Wires native-only behaviour: push notifications (after sign-in), Android
// hardware back button, and status-bar style. This component is a no-op when
// running in a regular browser tab (the native helpers internally guard).

const NativeBootstrap = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Status bar + back button — wire once on mount
  useEffect(() => {
    let unsubBack = () => {};
    (async () => {
      const { setStatusBarStyle, wireBackButton } = await import('./lib/native');
      // White content on coral header → use Light style
      setStatusBarStyle(true);
      unsubBack = await wireBackButton(navigate);
    })();
    return () => { try { unsubBack(); } catch (_) { /* ignore */ } };
  }, [navigate]);

  // Register for push notifications once the user is authenticated
  useEffect(() => {
    if (!user?.user_id) return;
    (async () => {
      const { registerForPush } = await import('./lib/native');
      await registerForPush(API);
    })();
  }, [user?.user_id]);

  return null;
};

// ============== Iter-102 · Deferred doctor-link consumer ==============
// When a patient scans a doctor's QR poster (`/join/XYZ`), `JoinDoctor.js`
// stashes the clinic code in localStorage and (on native) the OS
// pasteboard. The original design relied on the Onboarding flow to
// consume that stash — but if a returning patient signs up straight
// from the login screen (skipping onboarding), or a native-app patient
// installs from a Play-Store deep link and lands on the home screen,
// the pending code was never claimed. This component runs on every
// successful auth event, so wherever the patient lands, they get
// auto-connected to their doctor as soon as they're signed in.
const PendingClinicLinkConsumer = () => {
  const { user, checkAuth } = useAuth();
  // Guard against double-firing if `user` re-renders while we're still
  // in-flight (React StrictMode / hot reload / user-details refetch).
  const inflight = React.useRef(false);

  useEffect(() => {
    if (!user?.user_id) return;
    // Doctors never get auto-linked to another clinic.
    if (user.role === 'doctor') return;
    // Already linked to *someone* — leave them alone.
    if (user.linked_doctor_id) return;
    if (inflight.current) return;

    (async () => {
      inflight.current = true;
      try {
        let pendingCode = null;
        let pendingInvite = null;
        try {
          pendingCode = localStorage.getItem('pending_clinic_code');
          pendingInvite = localStorage.getItem('pending_clinic_invite');
        } catch { /* private mode / disabled storage */ }

        // Fallback: peek the OS clipboard for the `aayi:join:XXX` /
        // `aayi:invite:xxx` marker that JoinDoctor writes right before
        // it redirects to the App Store. Native app patients who arrive
        // via a deferred deep-link often have nothing in localStorage
        // (different origin / freshly-installed WebView), so this is
        // their only breadcrumb.
        if (!pendingCode && !pendingInvite && navigator?.clipboard?.readText) {
          try {
            const clip = await navigator.clipboard.readText();
            const m = clip?.match(/^aayi:(join|invite):([A-Za-z0-9_]+)$/);
            if (m) {
              if (m[1] === 'invite') pendingInvite = m[2];
              else pendingCode = m[2].toUpperCase();
              try { await navigator.clipboard.writeText(''); } catch { /* noop */ }
            }
          } catch { /* clipboard permission denied — silent */ }
        }
        if (!pendingCode && !pendingInvite) return;

        const joinedViaQr = (() => { try { return localStorage.getItem('joined_via_qr') === '1'; } catch { return false; } })();

        let doctorName = 'your doctor';
        if (pendingInvite) {
          const r = await axios.post(`${API}/patient/link-invite`,
            { invite_token: pendingInvite },
            { withCredentials: true });
          doctorName = r.data?.doctor_name || doctorName;
        } else if (pendingCode) {
          const r = await axios.post(`${API}/patient/link-clinic?joined_via_qr=${joinedViaQr}`,
            { clinic_code: pendingCode },
            { withCredentials: true });
          doctorName = r.data?.doctor_name || doctorName;
        }

        // Consume everything so a later auth event doesn't re-fire.
        try {
          localStorage.removeItem('pending_clinic_code');
          localStorage.removeItem('pending_clinic_invite');
          localStorage.removeItem('joined_via_qr');
        } catch { /* noop */ }

        toast.success(`You're now connected to ${doctorName}!`, { duration: 4000 });
        // Refresh /auth/me so the rest of the UI (dashboard, doctor
        // consultation buttons, etc.) sees `linked_doctor_id` set
        // without needing a hard reload.
        try { await checkAuth(); } catch { /* noop */ }
      } catch (e) {
        // Non-fatal: 404 (bad code) OR 400 (doctor account) — leave the
        // localStorage pending so the user can retry from Profile.
        console.warn('[PendingClinicLinkConsumer] auto-link skipped:', e?.response?.data?.detail || e?.message);
      } finally {
        inflight.current = false;
      }
    })();
  }, [user?.user_id, user?.linked_doctor_id, user?.role, checkAuth]);

  return null;
};

// Lightweight fallback shown while a lazy-loaded route chunk is being fetched.
// DOM-only (no shadcn) so the fallback itself has zero bundle cost.
const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    data-testid="route-suspense-fallback"
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <div
      style={{
        width: 28,
        height: 28,
        border: '3px solid #e5e7eb',
        borderTopColor: '#0ea5e9',
        borderRadius: '50%',
        animation: 'aayi-spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes aayi-spin { to { transform: rotate(360deg) } }`}</style>
  </div>
);


const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <LanguageAuthSync />
      <NativeBootstrap />
      <PendingClinicLinkConsumer />
      <BiometricGate />
      <BuildBadge />
      <Routes>
        {ROUTE_CONFIG.map(({ path, Component, protected: isProtected }) => (
          <Route
            key={path}
            path={path}
            element={
              <Suspense fallback={<RouteFallback />}>
                {isProtected
                  ? <ProtectedRoute><Component /></ProtectedRoute>
                  : <Component />}
              </Suspense>
            }
          />
        ))}
      </Routes>
    </>
  );
};

// Global handle the PaywallProvider sets when mounted, so the axios 402
// interceptor can open the in-app paywall (iOS native) instead of doing a
// hard navigation to /subscription. Falls back to nav if not yet mounted.
let __openPaywallGlobal = null;
export const __setOpenPaywallGlobal = (fn) => { __openPaywallGlobal = fn; };

// One-shot PostHog initialisation (no-op if REACT_APP_POSTHOG_KEY missing).
initPostHog();
initWebVitals();

// ============== GLOBAL AXIOS 402 HANDLER → paywall ==============
axios.interceptors.response.use(
  r => {
    // Native iOS/Android WebViews drop cross-site cookies — capture session_token
    // returned by auth endpoints and resend it as Authorization Bearer on every
    // subsequent request. Backend's require_auth already accepts both.
    // saveAuthToken() mirrors to native Preferences so the token survives iOS
    // ITP wiping localStorage on cold start.
    try {
      const t = r?.data?.session_token;
      if (t && typeof t === 'string' && t.length > 0) {
        saveAuthToken(t);
        // Auth responses carry the full user object — snapshot it immediately
        // so persistence works even if the app is killed right after login.
        if (r?.data?.user_id) saveCachedUser(r.data);
      }
      const rt = r?.data?.refresh_token;
      if (rt && typeof rt === 'string' && rt.length > 0) {
        saveRefreshToken(rt);
      }
    } catch {}
    return r;
  },
  err => {
    // Normalize `detail` to a string BEFORE downstream .catch() blocks run.
    // FastAPI can return `detail` as an object (e.g. 402 paywall returns
    // {error, feature, message, plans}). Many callers pass detail straight to
    // toast.error / JSX which renders it as a React child — crashing the tree
    // with "Objects are not valid as a React child" and blanking the page.
    try {
      const d = err?.response?.data?.detail;
      if (d && typeof d === 'object' && !Array.isArray(d)) {
        err.response.data.detail = d.message || d.error || 'Something went wrong';
      }
    } catch {}
    if (err?.response?.status === 402 && typeof window !== 'undefined') {
      // Marker so downstream .catch() blocks (in ~40 form-submit handlers) can
      // detect "the sheet already opened, don't fire another toast".
      err.__paywall_handled = true;
      // In-app paywall — bottom sheet on web, native RC sheet on iOS/Android.
      // No fallback navigation: keeping the user on the current route (which
      // still shows whatever content it can) is strictly better UX than
      // teleporting them to /subscription and losing their in-progress input.
      if (typeof __openPaywallGlobal === 'function') {
        // Show a subtle one-line toast; the sheet itself carries the CTA.
        const detail = err.response.data?.detail;
        const msg = (typeof detail === 'string' ? detail : null) || 'This is an Aayi Pro feature — opening upgrade sheet…';
        toast.info(msg, { duration: 2200 });
        setTimeout(() => { try { __openPaywallGlobal('http_402'); } catch {} }, 250);
      } else {
        const detail = err.response.data?.detail;
        const msg = (typeof detail === 'string' ? detail : null) || 'Upgrade to continue using this feature';
        toast.error(msg);
        if (!window.location.pathname.startsWith('/subscription')) {
          // Only fall back to a hard nav if the provider hasn't mounted yet
          // (e.g. an error fires during initial auth before <PaywallProvider>).
          setTimeout(() => { window.location.href = '/subscription'; }, 900);
        }
      }
    }
    return Promise.reject(err);
  }
);

// On startup, attach saved Bearer token to all requests so native WebViews work.
axios.interceptors.request.use((config) => {
  try {
    const t = getAuthToken();
    if (t) {
      config.headers = config.headers || {};
      // Don't override an explicit Authorization a caller already set.
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${t}`;
      }
    }
    // Wave 2.2 (CSRF double-submit): echo the csrf_token cookie into the
    // X-CSRF-Token header on state-changing requests. The backend
    // middleware only enforces this on cookie-only calls; Bearer requests
    // bypass, so this is a belt-and-braces safeguard.
    const method = (config.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method) && typeof document !== 'undefined') {
      const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      if (m) {
        config.headers = config.headers || {};
        if (!config.headers['X-CSRF-Token']) {
          config.headers['X-CSRF-Token'] = decodeURIComponent(m[1]);
        }
      }
      // Iter-88 (login-CSRF defence): stamp every state-changing request
      // with a custom X-Requested-With header. The backend requires this
      // on auth entry-points; a cross-origin `<form>` POST from an
      // attacker's page CANNOT set custom headers, and any fetch/XHR
      // trying to would fail the CORS preflight from a non-allow-listed
      // origin — so this header is effectively a "same-origin only" gate.
      config.headers = config.headers || {};
      if (!config.headers['X-Requested-With']) {
        config.headers['X-Requested-With'] = 'aayi-web';
      }
    }
  } catch {}
  return config;
});

// ============== OFFLINE BANNER ==============

const OfflineBanner = () => {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);

    // Capacitor Network — more reliable than navigator.onLine on iOS/Android
    let unsubNative = () => {};
    (async () => {
      const { subscribeNetworkStatus } = await import('./lib/native');
      unsubNative = await subscribeNetworkStatus(setOnline);
    })();

    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
      try { unsubNative(); } catch (_) { /* ignore */ }
    };
  }, []);
  if (online) return null;
  return (
    <div data-testid="offline-banner" className="fixed top-0 left-0 right-0 z-50 bg-[#B83826] text-white text-xs font-bold py-2 text-center shadow-lg">
      You're offline · showing cached content
    </div>
  );
};

// ============== MAIN APP ==============

function App() {
  return (
    <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <ClinicBrandProvider>
            <BrowserRouter>
              <PaywallProvider>
                <PaywallGlobalBinder />
                <SystemStatusBanner />
                <OfflineBanner />
                <AppRouter />
                <Toaster position="top-center" richColors />
              </PaywallProvider>
            </BrowserRouter>
          </ClinicBrandProvider>
        </AuthProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

// Bridges the PaywallContext (only available inside <PaywallProvider>) to the
// module-level axios interceptor via __setOpenPaywallGlobal.
const PaywallGlobalBinder = () => {
  const { openPaywall } = usePaywall();
  React.useEffect(() => {
    __setOpenPaywallGlobal(openPaywall);
    return () => __setOpenPaywallGlobal(null);
  }, [openPaywall]);
  return null;
};

export default App;
export { API };
